/**
 * @module domain/match
 * @description Pure giving and matching-gift math. No I/O, so the same
 * functions drive the hero panel, the ballroom screen, admin and (later)
 * the server-side totals.
 */
import type { Pledge } from "./types";

/** What was actually given. The Campaign edition shows only this. */
export interface GivingProgress {
    /** Dollars pledged in the app plus dollars recorded offline. */
    raised: number;
    /** Gifts made in the app plus gifts recorded offline ("friends who have given"). */
    donorCount: number;
}

/** Gala edition: the 1:1 match layered on top of what was given. */
export interface MatchProgress extends GivingProgress {
    /** Matching dollars unlocked (capped at the challenge amount). */
    unlocked: number;
    /** raised + unlocked — the total impact figure. */
    totalImpact: number;
    /** 0..1 share of the challenge unlocked. */
    fraction: number;
    remaining: number;
}

/**
 * Sums real gifts. No multiplier is ever applied here.
 *
 * @param offlineTotal - Dollars collected outside this app (paper cards
 *   keyed in later, checks). Explicit and visible in admin — never a hidden seed.
 * @param offlineCount - Number of those offline gifts.
 */
export function computeGivingProgress(pledges: readonly Pledge[], offlineTotal = 0, offlineCount = 0): GivingProgress {
    return {
        raised: pledges.reduce((sum, p) => sum + p.amount, 0) + offlineTotal,
        donorCount: pledges.length + offlineCount,
    };
}

/**
 * Computes challenge progress for a 1:1 match, from the same giving totals.
 * @param matchCap - Maximum matching dollars available.
 */
export function computeMatchProgress(pledges: readonly Pledge[], matchCap: number, offlineTotal = 0, offlineCount = 0): MatchProgress {
    const giving = computeGivingProgress(pledges, offlineTotal, offlineCount);
    const unlocked = Math.min(giving.raised, matchCap);
    return {
        ...giving,
        unlocked,
        totalImpact: giving.raised + unlocked,
        fraction: matchCap > 0 ? unlocked / matchCap : 0,
        remaining: Math.max(matchCap - unlocked, 0),
    };
}
